import { FC, ReactElement, useEffect, useId, useRef, useState } from 'react';

import { Button, ButtonVariant } from '@pp/core/src/components/controls/button/Button';
import { CurrencyConnected } from '@pp/core/src/components/currency/CurrencyConnected';
import { Loader } from '@pp/core/src/components/loader/Loader';
import { useAudioTrigger } from '@pp/core/src/controllers/audioController/hooks';
import { L10nText } from '@pp/core/src/services/uiTranslations/reactBindings/L10nText';
import { useGameStateSlice } from '@pp/core/src/store/slices/game/selectors';
import { GameStatus } from '@pp/core/src/store/slices/game/types';
import { useSettingsSlice } from '@pp/core/src/store/slices/settings/selectors';
import { useModifiers } from '@pp/core/src/styles/hooks/useModifiers';
import { getIsLanguageRTL } from '@pp/core/src/utils/language';

import { trimmedTime, truncateToTwoDecimal } from '../../../config/utils';
import { useInGameHistoryDataFetcher } from '../../../store/apiHooks/useInGameHistoryDataFetcher';
import { useGameDispatch } from '../../../store/GameStore';
import { useInGameHistorySlice } from '../../../store/slices/inGameHistory/selectors';
import { inGameHistoryActions } from '../../../store/slices/inGameHistory/slice';
import { useCurrencySymbol } from '../../gameGrid/hooks';
import { ErrorMessage } from '../errorMessage/ErrorMessage';
import { ScrollableWrapper } from '../scrollableWrapper/ScrollableWrapper';
import { SingleMultiplier } from '../singleMultiplier/SingleMultiplier';

import styles from './InGameHistoryConnected.module.css';
import { EntityId, EntityState } from '@reduxjs/toolkit';
import { InGameHistoryState, InGameHistoryTypes } from '../../../store/slices/inGameHistory/types';

export const InGameHistoryConnected: FC<{showStaticData?:boolean}> = ({showStaticData = false}) => {
  const id = useId();
  const loadedRef = useRef(false);
  const dispatch = useGameDispatch();
  const audioTrigger = useAudioTrigger();
  const currencySymbol = useCurrencySymbol();
  const locale = useSettingsSlice.currentLanguage();
  const isLanguageRTL = getIsLanguageRTL(locale);
  const currency = useGameStateSlice.getCurrency() ?? '';
  const currentLanguage = useSettingsSlice.currentLanguage();
  const isApiRejected = useInGameHistorySlice.getIsApiRejected();
  const inGameHistoryDataFetcher = useInGameHistoryDataFetcher();
  const inGameHistoryData = useInGameHistorySlice.getInGameHistory();
  const currentPageNumber = Math.round(inGameHistoryData.ids.length / 20);
  const [page, setPage] = useState<number>(currentPageNumber ? currentPageNumber : 1);
  const isGameStarted = useGameStateSlice.getCurrentStatus() === GameStatus.PlaceYourBets;
  const dummyData:EntityState<InGameHistoryTypes, EntityId> & InGameHistoryState = {
    "ids": [
        0,
        1,
        2,
        3,
        4,
        5
    ],
    "entities": {
        "0": {
            "uId": 0,
            "betAmount": 10,
            "winAmount": 0,
            "cashoutMul": -1,
            "crashMul": 1.04,
            "isGameCancelled": false,
            "placeBetTime": "12:50:00",
            "placeBetDate": "2025/08/28",
            "hashKey": "123",
            "encryptedResult": "123",
            "description": "123",
            "error": 123
        },
        "1": {
            "uId": 1,
            "betAmount": 10,
            "winAmount": 0,
            "cashoutMul": -1,
            "crashMul": 1,
            "isGameCancelled": false,
            "placeBetTime": "12:50:00",
            "placeBetDate": "2025/08/28",
            "hashKey": "123",
            "encryptedResult": "123",
            "description": "123",
            "error": 123
        },
        "2": {
            "uId": 2,
            "betAmount": 50,
            "winAmount": 125,
            "cashoutMul": 2.50,
            "crashMul": 2.50,
            "isGameCancelled": false,
            "placeBetTime": "12:38:00",
            "placeBetDate": "2025/08/28",
            "hashKey": "123",
            "encryptedResult": "123",
            "description": "123",
            "error": 123
        },
        "3": {
            "uId": 3,
            "betAmount": 0.1,
            "winAmount": 0,
            "cashoutMul": -1,
            "crashMul": 2.09,
            "isGameCancelled": false,
            "placeBetTime": "12:38:00",
            "placeBetDate": "2025/08/28",
            "hashKey": "123",
            "encryptedResult": "123",
            "description": "123",
            "error": 123
        },
        "4": {
            "uId": 4,
            "betAmount": 10,
            "winAmount": 0,
            "cashoutMul": -1,
            "crashMul": 2.2,
            "isGameCancelled": false,
            "placeBetTime": "12:30:00",
            "placeBetDate": "2025/08/28",
            "hashKey": "123",
            "encryptedResult": "123",
            "description": "123",
            "error": 123
        },
        "5": {
            "uId": 5,
            "betAmount": 10,
            "winAmount": 0,
            "cashoutMul": -1,
            "crashMul": 2.2,
            "isGameCancelled": false,
            "placeBetTime": "12:15:00",
            "placeBetDate": "2025/08/28",
            "hashKey": "123",
            "encryptedResult": "123",
            "description": "123",
            "error": 123
        }
    },
    "loading": false,
    "loadMore": false,
    "page": 1,
    "error": false
}
const _inGameHistoryData = showStaticData ? dummyData : inGameHistoryData;

  const mods = useModifiers(
    styles,
    'inGameHistory_header_row',
    {
      small: currency === 'mBTC' || ['bn', 'hi', 'ar'].includes(currentLanguage),
      rtl: showStaticData ? false : isLanguageRTL,
    },
    { withBaseClass: true }
  );

  useEffect(() => {
    if (!isApiRejected && !_inGameHistoryData.loading) {
      loadedRef.current = true;
    } else {
      loadedRef.current = false;
    }
  }, [isApiRejected, _inGameHistoryData.loading]);

  useEffect(() => {
    inGameHistoryDataFetcher(page, true);
  }, []);

  useEffect(() => {
    if (!isApiRejected && isGameStarted) {
      inGameHistoryDataFetcher(page, true);
    }
  }, [isApiRejected, isGameStarted]);

  const staticMods = useModifiers(
    styles,
    'inGameHistory_table',
    {
      static: showStaticData,
    },
    { withBaseClass: true }
  );

  return (
    <ScrollableWrapper>
      {((): ReactElement => {
        if (isApiRejected && !_inGameHistoryData.ids.length) {
          return (
            <ErrorMessage
              title="HIGHFLYER_FAILED_TO_LOAD"
              message="HIGHFLYER_INTERNAL_ERROR"
              disabled={false}
              loading={false}
              onClick={(): void => {
                dispatch(inGameHistoryActions.setApiRejected());
                inGameHistoryDataFetcher(page, true);
              }}
            />
          );
        } else if (!isApiRejected && !_inGameHistoryData.ids.length && _inGameHistoryData.loading && !loadedRef.current) {
          return <Loader />;
        } else {
          return (
            <>
              <table className={staticMods}>
                <div className={styles.header_border}></div>
                <thead className={styles.inGameHistory_header}>
                  <tr className={mods}>
                    <td className={styles.firstCol}>
                      {showStaticData ? 'Time' : <L10nText tk="TIME" />}
                    </td>
                    <td className={styles.secondCol}>
                      {showStaticData ? 'Bet' : <L10nText tk="BETLABEL" />}
                      <span>&nbsp;{currencySymbol}</span>
                    </td>
                    <td className={styles.thirdCol}></td>
                    <td className={styles.fourthCol}>
                      {showStaticData ? 'Cashed' : <L10nText tk="CASHED" />}
                      <span>&nbsp;{currencySymbol}</span>
                    </td>
                  </tr>
                </thead>
                <tbody className={styles.inGameHistory_body}>
                  {_inGameHistoryData.ids.map((uid: number | string) => {
                    return (
                      <tr
                        key={`in-game-history-${id}`}
                        className={`${styles.inGameHistory_body_row} ${Number(_inGameHistoryData.entities?.[uid]?.winAmount) > 0 && Number(_inGameHistoryData.entities?.[uid]?.cashoutMul) > 0 && styles.inGameHistory_body_row_active}`}
                      >
                        <td className={styles.firstCol}>
                          {trimmedTime(_inGameHistoryData.entities?.[uid]?.placeBetTime ?? '')}
                        </td>
                        <td className={styles.secondCol}>
                          <CurrencyConnected
                            value={Number(_inGameHistoryData.entities?.[uid]?.betAmount)}
                            hideCurrency
                          />
                        </td>
                        <td
                          /* style={{
                            cursor:
                              Number(inGameHistoryData.entities?.[uid]?.cashoutMul) > 0 ||
                              inGameHistoryData.entities?.[uid]?.isGameCancelled
                                ? 'auto'
                                : 'pointer',
                          }} */
                          className={styles.thirdCol}
                          /*  onClick={
                            Number(inGameHistoryData.entities?.[uid]?.cashoutMul) < 0
                              ? (): void => {
                                audioTrigger.play({ name: 'menuClick' });
                                openHashModel(
                                  Number(inGameHistoryData.entities?.[uid]?.uId),
                                  'history',
                                  Number(inGameHistoryData.entities?.[uid]?.crashMul),
                                  inGameHistoryData.entities?.[uid]?.hashKey ?? '',
                                  inGameHistoryData.entities?.[uid]?.encryptedResult ?? '',
                                  inGameHistoryData.entities?.[uid]?.placeBetDate.concat(
                                    ' ',
                                    inGameHistoryData.entities?.[uid]?.placeBetTime ?? ''
                                  ) ?? ''
                                );
                              }
                              : undefined
                          } */
                        >
                          {!_inGameHistoryData.entities?.[uid]?.isGameCancelled && (
                            <SingleMultiplier
                              value={
                                Number(_inGameHistoryData.entities?.[uid]?.cashoutMul) > 0
                                  ? Number(_inGameHistoryData.entities?.[uid]?.cashoutMul)
                                  : Number(_inGameHistoryData.entities?.[uid]?.crashMul)
                              }
                              plain={Number(_inGameHistoryData.entities?.[uid]?.cashoutMul) <= 0}
                            />
                          )}
                        </td>
                        <td
                          className={`${styles.fourthCol} ${Number(_inGameHistoryData.entities?.[uid]?.winAmount) > 0 && Number(_inGameHistoryData.entities?.[uid]?.cashoutMul) > 0 && styles.fourthCol_won}`}
                        >
                          <CurrencyConnected
                            value={truncateToTwoDecimal(Number(_inGameHistoryData.entities?.[uid]?.winAmount))}
                            hideCurrency
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {_inGameHistoryData.ids.length > 0 && _inGameHistoryData.loadMore && (
                <div className={styles.loadMore}>
                  <Button
                    tk="LOAD_MORE"
                    variant={ButtonVariant.MAIN}
                    disabled={!_inGameHistoryData.loadMore}
                    loading={_inGameHistoryData.loading}
                    onClick={() => {
                      setPage((prev) => prev + 1);
                      audioTrigger.play({ name: 'menuClick' });
                      inGameHistoryDataFetcher(page + 1, true);
                    }}
                    className={styles.buttonBg}
                  ></Button>
                </div>
              )}
            </>
          );
        }
      })()}
    </ScrollableWrapper>
  );
};
